import type { CustomField } from "@/react/prop-types";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import { EquipmentAssignmentStore } from "@/stores/equipment-assignments-store.core";
import { EquipmentRegistryStore } from "@/stores/equipment-registry-store.core";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import type { ResponsePayload } from "@laborchart-modules/common/dist/api";
import type { SerializedEquipmentAssignment } from "@laborchart-modules/common/dist/postgres/serializers/equipment-assignment-serializer";
import type { UpdateSingleAssignmentResponse } from "@laborchart-modules/lc-core-api/dist/api/assignments";
import type { BatchCreateAssignmentsResponse } from "@laborchart-modules/lc-core-api/dist/api/assignments/create-assignment";
import { getCommonFieldsForRequestAndAssignment, transformOvertimeRates } from "../helper";
import type {
   AssignmentFormValues,
   BatchResourceAssignment,
   SingleResourceAssignment,
   BatchEquipmentAssignment,
   SingleEquipmentAssignment,
} from "./types";

export interface SubmitAssignmentConfig {
   authorId: string;
   customFields: CustomField[] | null;
   isCreateFlow: boolean;
   assignmentId: string | undefined;
   projectProcoreId: number | null;
   procoreCompanyId: string | undefined;
}

export type LaborAssignmentResult =
   | UpdateSingleAssignmentResponse
   | BatchCreateAssignmentsResponse
   | null;

export type EquipmentAssignmentResult =
   | ResponsePayload<SerializedEquipmentAssignment[]>
   | ResponsePayload<SerializedEquipmentAssignment>
   | null;

export interface SubmitAssignmentResult {
   laborResult: LaborAssignmentResult;
   equipmentResult: EquipmentAssignmentResult;
}

/**
 * Transforms form values into labor and equipment assignment payloads,
 *  branching on `isCreateFlow` for single vs. batch shapes.
 * @param values the assignment form values including resource/equipment selections
 * @param config subset of submit config
 * @returns labor and equipment payloads with only those matching the current flow and selections being non-null
 */
export function buildAssignments(
   values: AssignmentFormValues,
   config: Pick<SubmitAssignmentConfig, "authorId" | "customFields" | "isCreateFlow">,
): {
   singleLaborAssignment: SingleResourceAssignment | null;
   batchLaborAssignment: BatchResourceAssignment | null;
   singleEquipmentAssignment: SingleEquipmentAssignment | null;
   batchEquipmentAssignment: BatchEquipmentAssignment | null;
} {
   const formValues = { ...values, author_id: config.authorId };
   const commonFields = getCommonFieldsForRequestAndAssignment(formValues, config.customFields);

   const commonFieldsForResourceAndEquipment = {
      overtime: formValues.assignment_by_time ? formValues.overtime : false,
      pay_split:
         formValues.overtime && formValues.assignment_by_time
            ? {
                 overtime: formValues.pay_split?.overtime ?? 0,
                 straight: formValues.pay_split?.straight ?? 0,
                 unpaid: formValues.pay_split?.unpaid ?? 0,
              }
            : null,
      overtime_rates:
         formValues.overtime && formValues.assignment_by_time
            ? transformOvertimeRates(formValues.overtime_rates)
            : null,
   };

   let singleLaborAssignment: SingleResourceAssignment | null = null;
   let batchLaborAssignment: BatchResourceAssignment | null = null;
   let singleEquipmentAssignment: SingleEquipmentAssignment | null = null;
   let batchEquipmentAssignment: BatchEquipmentAssignment | null = null;

   // Batch labor assignment (create flow with multiselect)
   if (
      Array.isArray(formValues.resource) &&
      formValues.resource.length > 0 &&
      config.isCreateFlow
   ) {
      batchLaborAssignment = {
         resource_ids: formValues.resource.map((res) => res.id),
         ...commonFields,
         ...commonFieldsForResourceAndEquipment,
      };
   }
   // Single labor assignment (edit flow with single select)
   else if (formValues.resource && !Array.isArray(formValues.resource) && !config.isCreateFlow) {
      singleLaborAssignment = {
         resource_id: formValues.resource.id,
         ...commonFields,
         ...commonFieldsForResourceAndEquipment,
      };
   }

   // Batch equipment assignment (create flow with multiselect)
   if (
      Array.isArray(formValues.equipment) &&
      formValues.equipment.length > 0 &&
      config.isCreateFlow
   ) {
      batchEquipmentAssignment = {
         resource_ids: formValues.equipment.map((e) => e.id),
         ...commonFields,
         ...commonFieldsForResourceAndEquipment,
      };
   }
   // Single equipment assignment (edit flow with single select)
   else if (formValues.equipment && !Array.isArray(formValues.equipment) && !config.isCreateFlow) {
      singleEquipmentAssignment = {
         resource_id: formValues.equipment.id,
         ...commonFields,
         ...commonFieldsForResourceAndEquipment,
      };
   }

   return {
      singleLaborAssignment,
      batchLaborAssignment,
      singleEquipmentAssignment,
      batchEquipmentAssignment,
   };
}

/**
 * Executes labor assignment API calls
 * @param singleLaborAssignment payload for updating a single labor assignment, if applicable
 * @param batchLaborAssignment payload for batch-creating labor assignments, if applicable
 * @param config subset of submit config
 * @returns the first resolved labor API response, or `null` if no labor assignments were provided
 */
export async function executeLaborAssignment(
   singleLaborAssignment: SingleResourceAssignment | null,
   batchLaborAssignment: BatchResourceAssignment | null,
   config: Pick<SubmitAssignmentConfig, "isCreateFlow" | "assignmentId">,
): Promise<LaborAssignmentResult> {
   const laborCalls: Array<
      Promise<UpdateSingleAssignmentResponse | BatchCreateAssignmentsResponse>
   > = [];

   if (singleLaborAssignment && !config.isCreateFlow && config.assignmentId) {
      laborCalls.push(
         Assignment2Store.updateSingleAssignment({
            assignmentId: config.assignmentId,
            update: singleLaborAssignment,
         }).payload,
      );
   }

   if (batchLaborAssignment && config.isCreateFlow) {
      laborCalls.push(Assignment2Store.createBatchAssignment(batchLaborAssignment).payload);
   }

   if (laborCalls.length > 0) {
      const laborResults = await Promise.all(laborCalls);
      return laborResults[0] ?? null;
   }

   return null;
}

/**
 * Executes equipment assignment API calls.
 * On batch create, also associates equipment to the Procore project
 *  if Procore IDs are available.
 * @param singleEquipmentAssignment payload for updating a single equipment assignment, if applicable
 * @param batchEquipmentAssignment payload for batch-creating equipment assignments, if applicable
 * @param config subset of submit config
 * @returns the equipment API response, or `null` if no equipment assignments were provided
 */
export async function executeEquipmentAssignment(
   singleEquipmentAssignment: SingleEquipmentAssignment | null,
   batchEquipmentAssignment: BatchEquipmentAssignment | null,
   config: Pick<SubmitAssignmentConfig, "assignmentId" | "projectProcoreId" | "procoreCompanyId">,
): Promise<EquipmentAssignmentResult> {
   if (singleEquipmentAssignment && config.assignmentId) {
      return EquipmentAssignmentStore.updateSingleEquipmentAssignment({
         equipmentAssignmentId: config.assignmentId,
         update: singleEquipmentAssignment,
      }).payload;
   }

   if (batchEquipmentAssignment) {
      const equipmentResult = await EquipmentAssignmentStore.batchCreateEquipmentAssignments(
         batchEquipmentAssignment,
      ).payload;

      if (config.projectProcoreId && config.procoreCompanyId) {
         try {
            await EquipmentRegistryStore.associateEquipmentToProject(
               config.procoreCompanyId,
               config.projectProcoreId.toString(),
               batchEquipmentAssignment.resource_ids,
            ).payload;
         } catch (error) {
            reportError(error as NotifiableError, "equipment-assignment__associate-to-project", {
               "association details": {
                  equipmentIds: batchEquipmentAssignment.resource_ids,
                  projectProcoreId: config.projectProcoreId,
               },
            });
         }
      }

      return equipmentResult;
   }

   return null;
}

/**
 * Orchestrates assignment submission:
 *  builds payloads via {@link buildAssignments},
 *  then executes labor and equipment API calls in sequence.
 * @param values the assignment form values.
 * @param config full submit configuration
 * @returns labor and equipment API results alongside the built payloads
 */
export async function submitAssignment(
   values: AssignmentFormValues,
   config: SubmitAssignmentConfig,
): Promise<SubmitAssignmentResult> {
   const {
      singleLaborAssignment,
      batchLaborAssignment,
      singleEquipmentAssignment,
      batchEquipmentAssignment,
   } = buildAssignments(values, config);

   const laborResult = await executeLaborAssignment(
      singleLaborAssignment,
      batchLaborAssignment,
      config,
   );

   const equipmentResult = await executeEquipmentAssignment(
      singleEquipmentAssignment,
      batchEquipmentAssignment,
      config,
   );

   return { laborResult, equipmentResult };
}
